import { useState, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

// Draggable Image Item Component
const DraggableImageItem = ({ 
  image, 
  index, 
  type, // 'existing' or 'new'
  onRemove, 
  moveImage,
  onSetPrimary,
  onSetAuctionSheet,
  isPrimary,
  isAuctionSheet,
  showAuctionSheetAction,
  disabled,
  itemKey,
}) => {
  const ref = useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: type === 'existing' ? 'existing-image' : 'new-image',
    item: { index, type, itemKey },
    canDrag: !disabled,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ['existing-image', 'new-image'],
    collect: monitor => ({
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop(),
    }),
    canDrop: draggedItem => draggedItem.type === type && !disabled,
    hover: (draggedItem, monitor) => {
      if (!ref.current || disabled) return;
      if (draggedItem.type !== type) return;

      const dragIndex = draggedItem.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;

      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveImage(dragIndex, hoverIndex, type);
      draggedItem.index = hoverIndex;
    },
  });

  drag(drop(ref));

  return (
    <div 
      ref={ref}
      data-reorder-key={itemKey}
      className={`card mb-2 image-upload__item ${
        isDragging ? 'image-upload__item--dragging' : ''
      } ${
        isOver && canDrop && !isDragging ? 'image-upload__item--over' : ''
      }`}
      style={{ cursor: disabled ? 'not-allowed' : 'grab' }}
    >
      <div className="card-body py-2">
        <div className="d-flex align-items-center">
          <div className="flex-shrink-0 me-3 position-relative">
            <img
              src={type === 'existing' ? image.url : image.preview}
              alt={type === 'existing' ? `Image ${index + 1}` : image.name}
              className="rounded"
              style={{ width: "80px", height: "80px", objectFit: "cover" }}
            />
            {isPrimary && (
              <span className="position-absolute top-0 start-0 badge bg-success">
                Primary
              </span>
            )}
            {isAuctionSheet && (
              <span className="position-absolute bottom-0 start-0 badge bg-info">
                Auction
              </span>
            )}
          </div>
          
          <div className="flex-grow-1 overflow-hidden">
            {type === 'existing' ? (
              <>
                <h6 className="mb-1">Image #{index + 1}</h6>
                <p className="mb-0 text-muted">ID: {image.id}</p>
              </>
            ) : (
              <>
                <h6 className="mb-1 text-truncate">{image.name}</h6>
                <p className="mb-0 text-muted">{image.formattedSize}</p>
              </>
            )}
          </div>
          
          <div className="d-flex align-items-center gap-2">
            {/* Set Primary Button */}
            {!isPrimary && !isAuctionSheet && (
              <button
                type="button"
                className="btn btn-sm btn-outline-warning"
                onClick={() => onSetPrimary(image.id, type)}
                disabled={disabled}
                title="Set as primary image"
              >
                <i className="bx bx-star"></i>
              </button>
            )}
            
            {/* Primary Indicator */}
            {isPrimary && !isAuctionSheet && (
              <span className="badge bg-warning text-dark">
                <i className="bx bx-star me-1"></i> Primary
              </span>
            )}

            {showAuctionSheetAction && !isAuctionSheet && !isPrimary && (
              <button
                type="button"
                className="btn btn-sm btn-outline-info"
                onClick={() => onSetAuctionSheet(image.id, type)}
                disabled={disabled}
                title="Set as auction sheet"
              >
                <i className="bx bx-file"></i>
              </button>
            )}

            {showAuctionSheetAction && isAuctionSheet && (
              <span className="badge bg-info text-dark">
                <i className="bx bx-file me-1"></i> Auction
              </span>
            )}
            
            {/* Drag Handle */}
            <i className="bx bx-menu text-muted" style={{ fontSize: '1.5rem' }} />
            
            {/* Remove Button */}
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={() => onRemove(index, type, image.id)}
              disabled={disabled}
            >
              <i className="bx bx-trash"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main ImageUpload Component
const ImageUpload = ({
  existingImages = [],
  onImagesChange,
  maxFiles = 5,
  maxSizeMB = 5,
  acceptedFileTypes = "image/*",
  multiple = true,
  title = "Upload Images",
  subtitle = "Drag & drop images here or click to browse",
  showAuctionSheetAction = false,
  disabled = false
}) => {
  const [newFiles, setNewFiles] = useState([]);
  const [orderedExistingImages, setOrderedExistingImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [deletedIds, setDeletedIds] = useState([]);
  const listRef = useRef(null);
  const previousPositionsRef = useRef(new Map());

  // Initialize ordered existing images
  useEffect(() => {
    setOrderedExistingImages(existingImages);
  }, [existingImages]);

  // Format file size
  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  const captureListPositions = useCallback(() => {
    const list = listRef.current;
    if (!list) return;

    previousPositionsRef.current = new Map(
      Array.from(list.querySelectorAll("[data-reorder-key]")).map(node => [
        node.dataset.reorderKey,
        node.getBoundingClientRect(),
      ])
    );
  }, []);

  const animateListPositions = useCallback(() => {
    const previousPositions = previousPositionsRef.current;

    requestAnimationFrame(() => {
      const list = listRef.current;
      if (!list) return;

      list.querySelectorAll("[data-reorder-key]").forEach(node => {
        const previous = previousPositions.get(node.dataset.reorderKey);
        if (!previous) return;

        const next = node.getBoundingClientRect();
        const deltaX = previous.left - next.left;
        const deltaY = previous.top - next.top;

        if (!deltaX && !deltaY) return;

        node.style.transition = "none";
        node.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
        node.style.zIndex = "2";

        requestAnimationFrame(() => {
          node.style.transition =
            "transform 180ms cubic-bezier(0.2, 0, 0, 1), border-color 180ms ease, box-shadow 180ms ease, opacity 180ms ease";
          node.style.transform = "";

          window.setTimeout(() => {
            node.style.zIndex = "";
          }, 200);
        });
      });
    });
  }, []);

  // Move image in the list
  const moveImage = useCallback((fromIndex, toIndex, type) => {
    captureListPositions();

    if (type === 'existing') {
      setOrderedExistingImages(prev => {
        const newImages = [...prev];
        const [movedItem] = newImages.splice(fromIndex, 1);
        newImages.splice(toIndex, 0, movedItem);
        
        // Notify parent of reordering
        onImagesChange?.({
          allImages: getAllImages(newImages, newFiles),
          reorderedImages: newImages,
          deletedIds,
          primaryImageId: getPrimaryImageId(newImages)
        });

        animateListPositions();
        
        return newImages;
      });
    } else {
      setNewFiles(prev => {
        const newFilesList = [...prev];
        const [movedItem] = newFilesList.splice(fromIndex, 1);
        newFilesList.splice(toIndex, 0, movedItem);
        
        // Notify parent of reordering
        onImagesChange?.({
          allImages: getAllImages(orderedExistingImages, newFilesList),
          newFiles: newFilesList,
          deletedIds,
          primaryImageId: getPrimaryImageId(orderedExistingImages)
        });

        animateListPositions();
        
        return newFilesList;
      });
    }
  }, [
    orderedExistingImages,
    newFiles,
    deletedIds,
    onImagesChange,
    captureListPositions,
    animateListPositions,
  ]);

  // Get all images combined
  const getAllImages = (existing, newFilesList) => {
    return [
      ...existing.map(img => ({ ...img, type: 'existing' })),
      ...newFilesList.map(file => ({ ...file, type: 'new' }))
    ];
  };

  // Get primary image ID
  const getPrimaryImageId = (images) => {
    const primary = images.find(img => img.isPrimary);
    return primary?.id;
  };

  // Handle file processing
  const handleAcceptedFiles = useCallback((files) => {
    if (disabled) return;

    // Check if exceeding max files
    if (orderedExistingImages.length + newFiles.length + files.length > maxFiles) {
      alert(`You can only upload up to ${maxFiles} files`);
      return;
    }

    // Check file sizes
    const oversizedFiles = files.filter(file => file.size > maxSizeMB * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      alert(`Some files exceed the maximum size of ${maxSizeMB}MB`);
      return;
    }

    // Process valid files
    const validFiles = files.filter(file => file.size <= maxSizeMB * 1024 * 1024);
    
    const processedFiles = validFiles.map((file) => ({
      file,
      name: file.name,
      preview: URL.createObjectURL(file),
      formattedSize: formatBytes(file.size),
      id: `new-${Date.now()}-${Math.random()}`, // Temporary ID
      isPrimary: false,
      isAuctionSheet: false
    }));

    const updatedNewFiles = [...newFiles, ...processedFiles];
    setNewFiles(updatedNewFiles);
    
    // Notify parent component
    onImagesChange?.({
      allImages: getAllImages(orderedExistingImages, updatedNewFiles),
      newFiles: updatedNewFiles,
      deletedIds,
      primaryImageId: getPrimaryImageId(orderedExistingImages)
    });
  }, [orderedExistingImages, newFiles, maxFiles, maxSizeMB, onImagesChange, deletedIds, disabled]);

  // Handle remove
  const removeFile = (index, type, imageId) => {
    if (disabled) return;

    if (type === 'existing') {
      // Mark for deletion
      setDeletedIds(prev => [...prev, imageId]);
      
      // Remove from display
      const updatedExisting = orderedExistingImages.filter((_, i) => i !== index);
      setOrderedExistingImages(updatedExisting);
      
      // Notify parent
      onImagesChange?.({
        allImages: getAllImages(updatedExisting, newFiles),
        reorderedImages: updatedExisting,
        deletedIds: [...deletedIds, imageId],
        primaryImageId: getPrimaryImageId(updatedExisting)
      });
    } else {
      // Clean up preview URL
      URL.revokeObjectURL(newFiles[index].preview);
      
      // Remove new file
      const updatedNewFiles = newFiles.filter((_, i) => i !== index);
      setNewFiles(updatedNewFiles);
      
      // Notify parent
      onImagesChange?.({
        allImages: getAllImages(orderedExistingImages, updatedNewFiles),
        newFiles: updatedNewFiles,
        deletedIds,
        primaryImageId: getPrimaryImageId(orderedExistingImages)
      });
    }
  };

  // Handle set primary image
  const handleSetPrimary = (imageId, imageType = 'existing') => {
    if (disabled) return;

    const updatedExisting = orderedExistingImages.map(img => ({
      ...img,
      isPrimary: imageType === 'existing' && img.id === imageId,
      isAuctionSheet:
        imageType === 'existing' && img.id === imageId
          ? false
          : img.isAuctionSheet
    }));
    const updatedNewFiles = newFiles.map(img => ({
      ...img,
      isPrimary: imageType === 'new' && img.id === imageId,
      isAuctionSheet:
        imageType === 'new' && img.id === imageId
          ? false
          : img.isAuctionSheet
    }));
    
    setOrderedExistingImages(updatedExisting);
    setNewFiles(updatedNewFiles);
    
    // Notify parent
    onImagesChange?.({
      allImages: getAllImages(updatedExisting, updatedNewFiles),
      reorderedImages: updatedExisting,
      newFiles: updatedNewFiles,
      deletedIds,
      primaryImageId: imageId
    });
  };

  // Handle set auction sheet image
  const handleSetAuctionSheet = (imageId, imageType = 'existing') => {
    if (disabled) return;

    const updatedExisting = orderedExistingImages.map(img => ({
      ...img,
      isPrimary:
        imageType === 'existing' && img.id === imageId
          ? false
          : img.isPrimary,
      isAuctionSheet: imageType === 'existing' && img.id === imageId
    }));
    const updatedNewFiles = newFiles.map(img => ({
      ...img,
      isPrimary:
        imageType === 'new' && img.id === imageId
          ? false
          : img.isPrimary,
      isAuctionSheet: imageType === 'new' && img.id === imageId
    }));

    setOrderedExistingImages(updatedExisting);
    setNewFiles(updatedNewFiles);

    // Notify parent
    onImagesChange?.({
      allImages: getAllImages(updatedExisting, updatedNewFiles),
      reorderedImages: updatedExisting,
      newFiles: updatedNewFiles,
      deletedIds,
      primaryImageId: getPrimaryImageId(updatedExisting),
      auctionSheetImageId: imageId
    });
  };

  // Clear all files
  const clearAllFiles = () => {
    if (disabled) return;

    // Clean up preview URLs
    newFiles.forEach(file => URL.revokeObjectURL(file.preview));
    
    // Mark all existing images for deletion
    const allExistingIds = orderedExistingImages.map(img => img.id);
    
    setNewFiles([]);
    setOrderedExistingImages([]);
    setDeletedIds(allExistingIds);
    
    onImagesChange?.({
      allImages: [],
      newFiles: [],
      reorderedImages: [],
      deletedIds: allExistingIds,
      primaryImageId: null
    });
  };

  // Drag & drop handlers
  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled) return;
    const files = Array.from(e.dataTransfer.files);
    handleAcceptedFiles(files);
  };

  // Handle file input
  const handleFileInputChange = (e) => {
    if (disabled) return;
    const files = Array.from(e.target.files);
    handleAcceptedFiles(files);
    e.target.value = ''; // Reset input
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="image-upload">
        <h6 className="card-title">{title}</h6>
        {subtitle && <p className="text-muted mb-3">{subtitle}</p>}
        
        {/* Dropzone */}
        <div 
          className={`dropzone ${isDragging ? 'dragging' : ''} ${disabled ? 'disabled' : ''}`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{ opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? 'none' : 'auto' }}
        >
          <div className="dz-message needsclick">
            <div className="mb-3">
              <i className="display-4 text-muted bx bxs-cloud-upload"></i>
            </div>
            <h5>Drop files here or click to upload</h5>
            <span className="text-muted font-size-13">
              (Max size: {maxSizeMB}MB, Max files: {maxFiles})
            </span>
            <input 
              type="file" 
              className="d-none" 
              id="fileInput"
              multiple={multiple}
              accept={acceptedFileTypes}
              onChange={handleFileInputChange}
              disabled={disabled}
            />
            <label 
              htmlFor="fileInput" 
              className={`btn btn-primary mt-3 ${disabled ? 'disabled' : ''}`}
              style={{ pointerEvents: disabled ? 'none' : 'auto' }}
            >
              Browse Files
            </label>
          </div>
        </div>
        
        {/* Image Lists */}
        {(orderedExistingImages.length > 0 || newFiles.length > 0) && (
          <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                All Images ({orderedExistingImages.length + newFiles.length})
                {deletedIds.length > 0 && (
                  <span className="badge bg-danger ms-2">
                    {deletedIds.length} marked for deletion
                  </span>
                )}
              </h6>
              {!disabled && (
                <button 
                  type="button" 
                  className="btn btn-sm btn-outline-danger"
                  onClick={clearAllFiles}
                >
                  Clear All
                </button>
              )}
            </div>
            
            <div className="selected-files image-upload__list" ref={listRef}>
              {/* Existing Images */}
              {orderedExistingImages.map((image, index) => (
                <DraggableImageItem
                  key={image.id}
                  itemKey={`existing-${image.id}`}
                  image={image}
                  index={index}
                  type="existing"
                  onRemove={removeFile}
                  moveImage={moveImage}
                  onSetPrimary={handleSetPrimary}
                  onSetAuctionSheet={handleSetAuctionSheet}
                  isPrimary={image.isPrimary}
                  isAuctionSheet={image.isAuctionSheet}
                  showAuctionSheetAction={showAuctionSheetAction}
                  disabled={disabled}
                />
              ))}
              
              {/* New Files */}
              {newFiles.map((file, index) => (
                <DraggableImageItem
                  key={file.id}
                  itemKey={`new-${file.id}`}
                  image={file}
                  index={index}
                  type="new"
                  onRemove={removeFile}
                  moveImage={moveImage}
                  onSetPrimary={handleSetPrimary}
                  onSetAuctionSheet={handleSetAuctionSheet}
                  isPrimary={file.isPrimary}
                  isAuctionSheet={file.isAuctionSheet}
                  showAuctionSheetAction={showAuctionSheetAction}
                  disabled={disabled}
                />
              ))}
            </div>
          </div>
        )}

        <style>{`
          .image-upload__list {
            position: relative;
          }

          .image-upload__item {
            border-color: #e8edf5;
            touch-action: none;
            transform-origin: center;
            transition:
              transform 180ms cubic-bezier(0.2, 0, 0, 1),
              border-color 180ms ease,
              box-shadow 180ms ease,
              opacity 180ms ease;
            user-select: none;
            will-change: transform;
          }

          .image-upload__item:hover {
            border-color: #cfd8ea;
            box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06);
          }

          .image-upload__item--dragging {
            border-color: #556ee6;
            box-shadow: 0 14px 30px rgba(85, 110, 230, 0.18);
            opacity: 0.46;
            transform: scale(1.015);
          }

          .image-upload__item--over {
            border-color: #9db4ff;
            box-shadow: inset 0 0 0 1px rgba(85, 110, 230, 0.18);
            transform: translateY(-1px);
          }

          .image-upload__item:active {
            cursor: grabbing !important;
          }

          @media (prefers-reduced-motion: reduce) {
            .image-upload__item {
              transition: none;
            }
          }
        `}</style>
      </div>
    </DndProvider>
  );
};

ImageUpload.propTypes = {
  existingImages: PropTypes.array,
  onImagesChange: PropTypes.func,
  maxFiles: PropTypes.number,
  maxSizeMB: PropTypes.number,
  acceptedFileTypes: PropTypes.string,
  multiple: PropTypes.bool,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  showAuctionSheetAction: PropTypes.bool,
  disabled: PropTypes.bool
};

export default ImageUpload;
